import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Paperclip, Upload } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import {
  client,
  deleteMedicationProfileAttachment,
  getMedicationAttachmentUrl,
  getMedicationProfileAttachments,
  uploadMedicationProfileAttachment,
} from '../../services/api';
import type { MedicationProfileAttachmentResponse } from '../../services/api';
import AttachmentRow from './AttachmentRow';
import AttachmentsEmptyState from './AttachmentsEmptyState';

interface AttachmentsCardProps {
  patientId: string;
}

const AttachmentsCard: React.FC<AttachmentsCardProps> = ({ patientId }) => {
  const [attachments, setAttachments] = useState<
    MedicationProfileAttachmentResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAttachments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getMedicationProfileAttachments({
        path: { patient_id: patientId },
        client,
      });
      if (response?.data) {
        setAttachments(response.data);
      } else {
        setAttachments([]);
      }
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const response = await uploadMedicationProfileAttachment({
        path: { patient_id: patientId },
        body: { file },
        client,
      });

      if (response?.data) {
        setAttachments(prev => [response.data!, ...prev]);
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadAttachment = async (
    attachment: MedicationProfileAttachmentResponse
  ) => {
    try {
      const response = await getMedicationAttachmentUrl({
        path: {
          patient_id: patientId,
          attachment_id: attachment.id,
        },
        client,
      });

      if (response?.data) {
        const data = response.data as { url?: string };
        if (data.url) {
          window.open(data.url, '_blank');
        }
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    await deleteMedicationProfileAttachment({
      path: {
        patient_id: patientId,
        attachment_id: attachmentId,
      },
      client,
    });

    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  return (
    <Card className='mt-4'>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Paperclip className='h-5 w-5' />
            <div>
              <CardTitle>Attachments</CardTitle>
              <CardDescription>
                Supporting documents for medication profile
              </CardDescription>
            </div>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type='file'
              className='hidden'
              onChange={handleFileSelect}
              accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif'
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              variant='outline'
            >
              {isUploading ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <Upload className='h-4 w-4 mr-2' />
              )}
              Upload
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        ) : attachments.length === 0 ? (
          <AttachmentsEmptyState />
        ) : (
          <div className='space-y-2'>
            {attachments.map(attachment => (
              <AttachmentRow
                key={attachment.id}
                attachment={attachment}
                onDownload={handleDownloadAttachment}
                onDelete={handleDeleteAttachment}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttachmentsCard;
